import { type PrnStatus } from "@src/appV2/redesign/PrnRoster/types";
import { type ShiftInviteStatus } from "@src/appV2/ShiftInvites/type";
import { type ComputedArrival } from "@src/appV2/Shifts/types";

import { type TimeSlot } from "../../Shift/types";
import { type ShiftSpecification } from "../../ShiftNotes/types";
import { type GenderRequirement } from "../components/ShiftPostingForm/types";
import { type SentHomeInfo } from "./parseSentHome";

/**
 * Shift state derived from shift data and shift logs.
 * Priority order (highest to lowest):
 * 1. sent-home - Worker was sent home by facility
 * 2. no-show-suspected - NCNS detected, replacement unfilled
 * 3. no-show-replaced - NCNS detected, replacement worker assigned
 * 4. worker-left-early - Worker left early (workerLeftEarly field set)
 * 5. running-late - Worker is running late (ETA after shift start, not yet clocked in)
 * 6. in-building - Worker entered geofence, shift started, not clocked in
 * 7. clocked-out - Worker has completed shift
 * 8. clocked-in - Worker is currently on shift
 * 9. scheduled - Worker is assigned, on time, hasn't clocked in yet
 * 10. invite-sent - Unfilled shift with a pending invite
 * 11. unfilled - Shift has no worker assigned
 */
export const SHIFT_STATES = {
  SENT_HOME: "sent-home",
  NO_SHOW_SUSPECTED: "no-show-suspected",
  NO_SHOW_REPLACED: "no-show-replaced",
  WORKER_LEFT_EARLY: "worker-left-early",
  RUNNING_LATE: "running-late",
  IN_BUILDING: "in-building",
  CLOCKED_OUT: "clocked-out",
  CLOCKED_IN: "clocked-in",
  SCHEDULED: "scheduled",
  UNFILLED: "unfilled",
  INVITE_SENT: "invite-sent",
} as const;

export type ShiftState = (typeof SHIFT_STATES)[keyof typeof SHIFT_STATES];

export const STATE_PRIORITY: Record<ShiftState, number> = {
  [SHIFT_STATES.SENT_HOME]: 1,
  [SHIFT_STATES.NO_SHOW_SUSPECTED]: 2,
  [SHIFT_STATES.NO_SHOW_REPLACED]: 3,
  [SHIFT_STATES.WORKER_LEFT_EARLY]: 4,
  [SHIFT_STATES.RUNNING_LATE]: 5,
  [SHIFT_STATES.IN_BUILDING]: 6,
  [SHIFT_STATES.CLOCKED_OUT]: 7,
  [SHIFT_STATES.CLOCKED_IN]: 8,
  [SHIFT_STATES.SCHEDULED]: 9,
  [SHIFT_STATES.INVITE_SENT]: 10,
  [SHIFT_STATES.UNFILLED]: 11,
};

export interface PendingInviteInfo {
  inviteId: string;
  shiftId?: string;
  workerName: string;
  workerId: string;
  workerAvatarUrl?: string;
  expiresAt: string;
  status?: ShiftInviteStatus;
  shiftDetails: {
    start: string;
    end: string;
    agentReq: string;
    timeSlot: string;
    tmz?: string;
  };
}

export interface ProfileImageSizes {
  small?: string;
  medium?: string;
  large?: string;
}

export interface ShiftPricingSettings {
  qualification: string;
  manualChargeInMinorUnits?: number;
  fillGuaranteeThresholdInMinorUnits?: number;
}

export interface ShiftCardData {
  shiftId: string;
  state: ShiftState;
  agentReq: string;
  postedQualificationName: string;
  startTime: string;
  endTime: string;
  timeSlot: TimeSlot;
  workerName?: string;
  workerId?: string;
  profileImageUrl?: string;
  profileImageSizes?: ProfileImageSizes;
  clockInTime?: string;
  clockOutTime?: string;
  /** ISO string of when the worker pre-registered their clock-in (before shift start) */
  clockInPreregisteredAt?: string;
  hasUrgency: boolean;
  /** Numeric urgency level from backend (1=NCNS, 2=LATE_CANCELLATION, 3=LAST_MINUTE) */
  urgencyLevel?: number;
  replacedShiftId?: string;
  sentHomeInfo?: SentHomeInfo;
  /** When the worker entered the facility geofence (ISO string) */
  geofenceEntryTime?: string;
  /** When the worker exited the facility geofence (ISO string) */
  geofenceExitTime?: string;
  /** Whether the worker has left the geofence */
  hasLeftGeofence?: boolean;
  /** Whether the worker is far from the facility */
  isWorkerFarFromFacility?: boolean;
  /** Worker left early info from the shift */
  workerLeftEarlyInfo?: {
    leftWithPermission: boolean;
    replacementRequested: boolean;
  };
  /** Previous worker info for replacement shifts */
  previousWorker?: {
    userId: string;
    name: string;
    profileImageUrl?: string;
  };
  /**
   * Label shown in the previous worker row of the two-row layout.
   * Set when a shift previously had a worker who canceled ("Canceled") or
   * had an invite that was declined and the shift was re-posted ("Declined invite").
   * When set, ShiftRowContent renders a two-row layout instead of a single row.
   */
  previousWorkerLabel?: "Canceled" | "Declined invite";
  /** Extended bookability time for unfilled shifts */
  extendBookabilityTo?: string;
  /** Computed arrival info for state computation (booking-time ETA, not for display) */
  computedArrival?: ComputedArrival;
  /** Pending invite info for unfilled shifts with active invites */
  pendingInvite?: PendingInviteInfo;
  /** When the shift was created/posted */
  createdAt?: string;
  /** Whether this shift has been verified (timecard processed) */
  verified?: boolean;
  /** Whether this shift has been deleted/canceled */
  deleted?: boolean;
  /** When the shift was canceled/deleted */
  deletedAt?: string;
  /** Who posted the shift */
  postedByName?: string;
  /** Whether a cancellation fee was charged (for deleted shifts) */
  isBillable?: boolean;
  /** Minutes it took to refill this replacement shift (computed from replacement pair data) */
  refilledInMinutes?: number;
  /** Shift specifications (e.g., requirements or special instructions shown to workers) */
  shiftSpecifications?: ShiftSpecification[];
  /** Gender restriction for the shift */
  genderRequirement?: GenderRequirement;
  pricingSettings?: ShiftPricingSettings[];
  /**
   * PRN-priority status for the shift's invite group, when one exists.
   * Drives the additive `Status: Roster*` chip in the daily view. `undefined`
   * for non-PRN shifts and for PRN shifts whose priority window has expired
   * with marketplace fall-through enabled (chip is dropped at that point).
   */
  prnStatus?: PrnStatus;
  /**
   * `groupId` on the underlying PRN posting, when one exists. Used so the
   * delete and edit-guard surfaces can address the posting directly. Carries
   * forward through `mapShiftToCard` and the virtual-card synth.
   */
  prnGroupId?: string;
}
